import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateHolidayBalance, HolidayBalanceDto } from "../../services/holidays.service.ts";
import { notification } from "../../utils/Notifications.tsx";

interface HolidayBalanceFormProps {
  close: () => void;
  onSuccess: () => void;
  balanceToEdit: {
    id: number;
    totalDays: number;
    daysTaken: number;
    daysPending: number;
    daysRemaining: number;
    notes?: string;
    holidayPeriod?: {
      startYear: number;
      endYear: number;
    };
  };
}

interface HolidayBalanceFormValues {
  totalDays: number;
  daysTaken: number;
  daysPending: number;
  notes: string;
}

const HolidayBalanceSchema = Yup.object().shape({
  totalDays: Yup.number()
    .min(0, "Los días totales no pueden ser negativos")
    .required("Los días totales son requeridos"),
  daysTaken: Yup.number()
    .min(0, "Los días usados no pueden ser negativos")
    .required("Los días usados son requeridos")
    .test(
      "max-days",
      "Los días usados no pueden ser mayores a los días totales",
      function (value) {
        const { totalDays } = this.parent;
        if (totalDays !== undefined && value !== undefined) {
          return value <= totalDays;
        }
        return true;
      }
    ),
  daysPending: Yup.number()
    .min(0, "Los días pendientes no pueden ser negativos")
    .required("Los días pendientes son requeridos"),
  notes: Yup.string(),
});

const HolidayBalanceForm = ({ close, onSuccess, balanceToEdit }: HolidayBalanceFormProps) => {
  const [loading, setLoading] = React.useState(false);

  const formik = useFormik<HolidayBalanceFormValues>({
    initialValues: {
      totalDays: balanceToEdit.totalDays || 0,
      daysTaken: balanceToEdit.daysTaken || 0,
      daysPending: balanceToEdit.daysPending || 0,
      notes: balanceToEdit.notes || "",
    },
    validationSchema: HolidayBalanceSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const updateData: HolidayBalanceDto = {
          totalDays: values.totalDays,
          daysTaken: values.daysTaken,
          daysPending: values.daysPending,
          notes: values.notes,
          idPartner: 0, // No se necesita para actualizar
          idHolidayPeriod: 0, // No se necesita para actualizar
        };

        await updateHolidayBalance(balanceToEdit.id, updateData);
        notification("Éxito", "Balance de vacaciones actualizado correctamente", "success");
        onSuccess();
      } catch (error) {
        console.error("Error updating balance:", error);
        notification("Error", "No se pudo actualizar el balance de vacaciones", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    formik.setValues({
      totalDays: balanceToEdit.totalDays || 0,
      daysTaken: balanceToEdit.daysTaken || 0,
      daysPending: balanceToEdit.daysPending || 0,
      notes: balanceToEdit.notes || "",
    });
  }, [balanceToEdit]);

  // Calcular días restantes automáticamente
  const calculatedDaysRemaining = formik.values.totalDays - formik.values.daysTaken;

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="p-4">
        {balanceToEdit.holidayPeriod && (
          <div className="alert alert-info mb-3">
            <i className="fas fa-calendar-alt me-2"></i>
            <strong>Período:</strong> {balanceToEdit.holidayPeriod.startYear} - {balanceToEdit.holidayPeriod.endYear}
          </div>
        )}

        <div className="mb-3">
          <label htmlFor="totalDays" className="form-label">
            Días Totales <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={`form-control ${formik.touched.totalDays && formik.errors.totalDays ? "is-invalid" : ""}`}
            id="totalDays"
            name="totalDays"
            value={formik.values.totalDays}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            min="0"
            disabled={loading}
          />
          {formik.touched.totalDays && formik.errors.totalDays && (
            <div className="invalid-feedback">{formik.errors.totalDays}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="daysTaken" className="form-label">
            Días Usados <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={`form-control ${formik.touched.daysTaken && formik.errors.daysTaken ? "is-invalid" : ""}`}
            id="daysTaken"
            name="daysTaken"
            value={formik.values.daysTaken}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            min="0"
            max={formik.values.totalDays}
            disabled={loading}
          />
          {formik.touched.daysTaken && formik.errors.daysTaken && (
            <div className="invalid-feedback">{formik.errors.daysTaken}</div>
          )}
          {formik.values.totalDays > 0 && (
            <small className="form-text text-muted">
              Máximo: {formik.values.totalDays} días
            </small>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="daysPending" className="form-label">
            Días Pendientes <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={`form-control ${formik.touched.daysPending && formik.errors.daysPending ? "is-invalid" : ""}`}
            id="daysPending"
            name="daysPending"
            value={formik.values.daysPending}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            min="0"
            disabled={loading}
          />
          {formik.touched.daysPending && formik.errors.daysPending && (
            <div className="invalid-feedback">{formik.errors.daysPending}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="daysRemaining" className="form-label">
            Días Restantes (calculado automáticamente)
          </label>
          <input
            type="number"
            className="form-control bg-light"
            id="daysRemaining"
            value={calculatedDaysRemaining}
            disabled
          />
          <small className="form-text text-muted">
            Días restantes = Días totales - Días usados
          </small>
        </div>

        <div className="mb-3">
          <label htmlFor="notes" className="form-label">
            Notas
          </label>
          <textarea
            className="form-control"
            id="notes"
            name="notes"
            rows={3}
            value={formik.values.notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={loading}
          />
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin me-1"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="fa fa-save me-1"></i>
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default HolidayBalanceForm;

